import { supabase } from '../lib/supabase';

/**
 * Service for quiz performance analytics with phoneme mastery tracking
 */

/**
 * Get comprehensive quiz performance breakdown for a user and play
 * @param {string} userId - User ID
 * @param {string} playId - Play ID
 * @returns {Promise<Object>} Performance breakdown with phoneme mastery rates, trends, and recommendations
 */
export const getQuizPerformanceBreakdown = async (userId, playId) => {
  try {
    // Fetch all related data in parallel
    const [
      phonemeMasteryResult,
      performanceSummaryResult,
      pronunciationAnalyticsResult,
      recentAttemptsResult
    ] = await Promise.all([
      // Phoneme mastery tracking
      supabase?.from('phoneme_mastery_tracking')?.select('*')?.eq('user_id', userId)?.eq('play_id', playId)?.order('updated_at', { ascending: false }),
      
      // Phoneme performance summary
      supabase?.from('phoneme_performance_summary')?.select('*')?.eq('user_id', userId)?.eq('play_id', playId),
      
      // Overall pronunciation analytics
      supabase?.from('pronunciation_analytics')?.select('*')?.eq('user_id', userId)?.eq('play_id', playId)?.order('date', { ascending: false })?.limit(30),
      
      // Recent quiz attempts
      supabase?.from('quiz_attempts')?.select('*')?.eq('user_id', userId)?.order('completed_at', { ascending: false })?.limit(10)
    ]);

    if (phonemeMasteryResult?.error) throw phonemeMasteryResult?.error;
    if (performanceSummaryResult?.error) throw performanceSummaryResult?.error;
    if (pronunciationAnalyticsResult?.error) throw pronunciationAnalyticsResult?.error;
    if (recentAttemptsResult?.error) throw recentAttemptsResult?.error;

    // Calculate phoneme mastery rates by category
    const phonemesByCategory = groupPhonemesByCategory(phonemeMasteryResult?.data);
    
    // Calculate pronunciation accuracy trends
    const accuracyTrends = calculateAccuracyTrends(pronunciationAnalyticsResult?.data);
    
    // Generate personalized recommendations
    const recommendations = await getPersonalizedRecommendations(userId, playId, phonemeMasteryResult?.data);

    return {
      phonemeMastery: {
        byCategory: phonemesByCategory,
        detailed: phonemeMasteryResult?.data?.map(pm => ({
          phoneme: pm?.phoneme,
          phonemeCategory: pm?.phoneme_category,
          accuracyScore: pm?.accuracy_score,
          attemptCount: pm?.attempt_count,
          firstAttemptScore: pm?.first_attempt_score,
          bestScore: pm?.best_score,
          recentAttempts: pm?.recent_attempts,
          improvementRate: pm?.improvement_rate,
          masteryLevel: pm?.mastery_level,
          updatedAt: pm?.updated_at
        }))
      },
      performanceSummary: performanceSummaryResult?.data?.map(ps => ({
        phonemeCategory: ps?.phoneme_category,
        totalPhonemes: ps?.total_phonemes,
        avgAccuracy: ps?.avg_accuracy,
        totalAttempts: ps?.total_attempts,
        masteredCount: ps?.mastered_count,
        proficientCount: ps?.proficient_count,
        practicingCount: ps?.practicing_count,
        learningCount: ps?.learning_count,
        avgImprovementRate: ps?.avg_improvement_rate,
        lastUpdated: ps?.last_updated
      })),
      accuracyTrends,
      recentAttempts: recentAttemptsResult?.data?.map(attempt => ({
        id: attempt?.id,
        quizId: attempt?.quiz_id,
        score: attempt?.score,
        totalQuestions: attempt?.total_questions,
        passed: attempt?.passed,
        timePerQuestion: attempt?.time_per_question,
        completedAt: attempt?.completed_at
      })),
      recommendations,
      overallStats: calculateOverallStats(
        phonemeMasteryResult?.data,
        pronunciationAnalyticsResult?.data,
        recentAttemptsResult?.data
      )
    };
  } catch (error) {
    console.error('Error fetching quiz performance breakdown:', error);
    throw new Error(`Failed to fetch quiz performance breakdown: ${error.message}`);
  }
};

/**
 * Group phonemes by category with mastery statistics
 * @param {Array} phonemes - Array of phoneme mastery records
 * @returns {Object} Phonemes grouped by category with statistics
 */
const groupPhonemesByCategory = (phonemes) => {
  const categories = {
    vowel: [],
    consonant: [],
    diphthong: [],
    combination: []
  };

  phonemes?.forEach(phoneme => {
    const category = phoneme?.phoneme_category;
    if (categories?.[category]) {
      categories?.[category]?.push({
        phoneme: phoneme?.phoneme,
        accuracyScore: phoneme?.accuracy_score,
        masteryLevel: phoneme?.mastery_level,
        improvementRate: phoneme?.improvement_rate
      });
    }
  });

  // Calculate category-level statistics
  return Object.keys(categories)?.reduce((acc, category) => {
    const categoryPhonemes = categories?.[category];
    if (categoryPhonemes?.length > 0) {
      const avgAccuracy = categoryPhonemes?.reduce((sum, p) => sum + p?.accuracyScore, 0) / categoryPhonemes?.length;
      const masteredCount = categoryPhonemes?.filter(p => p?.masteryLevel === 'mastered')?.length;
      const proficientCount = categoryPhonemes?.filter(p => p?.masteryLevel === 'proficient')?.length;
      
      acc[category] = {
        phonemes: categoryPhonemes,
        count: categoryPhonemes?.length,
        avgAccuracy: Math.round(avgAccuracy * 10) / 10,
        masteredCount,
        proficientCount,
        masteryRate: Math.round((masteredCount / categoryPhonemes?.length) * 100)
      };
    }
    return acc;
  }, {});
};

/**
 * Calculate pronunciation accuracy trends from recent analytics
 * @param {Array} analyticsData - Array of pronunciation analytics records
 * @returns {Object} Trends data with daily progress
 */
const calculateAccuracyTrends = (analyticsData) => {
  if (!analyticsData || analyticsData?.length === 0) {
    return {
      dailyProgress: [],
      overallTrend: 'stable',
      trendPercentage: 0
    };
  }

  const dailyProgress = analyticsData?.map(record => ({
    date: record?.date,
    averageScore: record?.average_score,
    totalAttempts: record?.total_attempts,
    improvementTrend: record?.improvement_trend,
    commonMistakes: record?.common_mistakes
  }));

  // Calculate overall trend
  if (dailyProgress?.length >= 2) {
    const firstScore = parseFloat(dailyProgress?.[dailyProgress?.length - 1]?.averageScore);
    const latestScore = parseFloat(dailyProgress?.[0]?.averageScore);
    const trendPercentage = ((latestScore - firstScore) / firstScore) * 100;
    
    let overallTrend = 'stable';
    if (trendPercentage > 5) overallTrend = 'improving';
    else if (trendPercentage < -5) overallTrend = 'declining';

    return {
      dailyProgress,
      overallTrend,
      trendPercentage: Math.round(trendPercentage * 10) / 10
    };
  }

  return {
    dailyProgress,
    overallTrend: 'stable',
    trendPercentage: 0
  };
};

/**
 * Get personalized recommendations based on phoneme mastery
 * @param {string} userId - User ID
 * @param {string} playId - Play ID
 * @param {Array} phonemeData - Current phoneme mastery data
 * @returns {Promise<Array>} Array of personalized recommendations
 */
const getPersonalizedRecommendations = async (userId, playId, phonemeData) => {
  try {
    // Call the database function to get recommendations
    const { data, error } = await supabase?.rpc('get_phoneme_recommendations', {
      p_user_id: userId,
      p_play_id: playId,
      p_limit: 5
    });

    if (error) throw error;

    return data?.map(rec => ({
      phoneme: rec?.phoneme,
      phonemeCategory: rec?.phoneme_category,
      accuracyScore: rec?.accuracy_score,
      attemptCount: rec?.attempt_count,
      improvementRate: rec?.improvement_rate,
      masteryLevel: rec?.mastery_level,
      recommendation: rec?.recommendation,
      priority: rec?.priority
    }));
  } catch (error) {
    console.error('Error fetching recommendations:', error);
    
    // Fallback: Generate basic recommendations from phoneme data
    const sortedPhonemes = [...phonemeData]?.sort((a, b) => a?.accuracy_score - b?.accuracy_score)?.slice(0, 5);

    return sortedPhonemes?.map(phoneme => ({
      phoneme: phoneme?.phoneme,
      phonemeCategory: phoneme?.phoneme_category,
      accuracyScore: phoneme?.accuracy_score,
      attemptCount: phoneme?.attempt_count,
      improvementRate: phoneme?.improvement_rate,
      masteryLevel: phoneme?.mastery_level,
      recommendation: phoneme?.accuracy_score < 70 
        ? 'Practice this phoneme with audio examples' :'Focus on pronunciation nuances',
      priority: phoneme?.accuracy_score < 50 ? 1 : phoneme?.accuracy_score < 70 ? 2 : 3
    }));
  }
};

/**
 * Calculate overall statistics from all performance data
 * @param {Array} phonemeData - Phoneme mastery data
 * @param {Array} analyticsData - Pronunciation analytics data
 * @param {Array} attemptsData - Quiz attempts data
 * @returns {Object} Overall performance statistics
 */
const calculateOverallStats = (phonemeData, analyticsData, attemptsData) => {
  const totalPhonemes = phonemeData?.length;
  const masteredPhonemes = phonemeData?.filter(p => p?.mastery_level === 'mastered')?.length;
  const avgAccuracy = phonemeData?.reduce((sum, p) => sum + p?.accuracy_score, 0) / (totalPhonemes || 1);
  
  const recentAnalytics = analyticsData?.[0];
  const practiceStreak = recentAnalytics?.practice_streak || 0;
  
  const totalQuizAttempts = attemptsData?.length;
  const passedQuizzes = attemptsData?.filter(a => a?.passed)?.length;
  const avgQuizScore = attemptsData?.reduce((sum, a) => sum + a?.score, 0) / (totalQuizAttempts || 1);

  return {
    totalPhonemes,
    masteredPhonemes,
    masteryPercentage: Math.round((masteredPhonemes / (totalPhonemes || 1)) * 100),
    avgAccuracy: Math.round(avgAccuracy * 10) / 10,
    practiceStreak,
    totalQuizAttempts,
    passedQuizzes,
    quizPassRate: Math.round((passedQuizzes / (totalQuizAttempts || 1)) * 100),
    avgQuizScore: Math.round(avgQuizScore)
  };
};

export default {
  getQuizPerformanceBreakdown
};